import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { 
  getDashboardSummary, 
  getDashboardKpis, 
  getDashboardCharts, 
  getDashboardMap,
  getRecentSessions,
  getBusinessWalletSummary
} from '../lib/api';
import { StatsCard } from '../components/common/StatsCard';
import { StatusBadge } from '../components/common/StatusBadge';
import usePageTitle from '../hooks/usePageTitle';
import { 
  MapPin, 
  Zap, 
  Users, 
  Activity, 
  Wallet,
  TrendingUp,
  Clock,
  Map,
  AlertTriangle,
  Battery,
  Wifi,
  WifiOff,
  RefreshCw,
  RefreshCcw
} from 'lucide-react';
import { formatCurrency, formatEnergy, formatDate } from '../lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Lazy load heavy components
const ChargerMap = lazy(() => import('../components/common/ChargerMap'));
const LiveSessionsMonitor = lazy(() => import('../components/dashboard/LiveSessionsMonitor'));

// Lazy load Recharts - only when needed
const LazyAreaChart = lazy(() => import('recharts').then(mod => ({ default: mod.AreaChart })));
const LazyPieChart = lazy(() => import('recharts').then(mod => ({ default: mod.PieChart })));

const COLORS = ['#afd039', '#1d62a7', '#f59e0b', '#ef4444', '#6b7280'];
const ONLINE_STATUSES = new Set([
  'online',
  'available',
  'charging',
  'preparing',
  'finishing',
  'suspendedev',
  'suspendedevse',
  'reserved'
]);

const parseBooleanLike = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'online'].includes(normalized)) return true;
    if (['false', '0', 'no', 'offline', ''].includes(normalized)) return false;
  }
  return false;
};

const isChargerOnline = (charger) => {
  if (!charger) return false;
  if (parseBooleanLike(charger.is_online) || parseBooleanLike(charger.online)) return true;
  const normalizedStatus = String(charger.status || '').trim().toLowerCase();
  return ONLINE_STATUSES.has(normalizedStatus);
};

const Dashboard = () => {
  usePageTitle('Dashboard');
  const formatLek = (amount) => formatCurrency(amount, 'Lek');
  const formatCredits = (amount) => formatCurrency(amount, 'Kredite');
  
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [charts, setCharts] = useState(null);
  const [mapData, setMapData] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);
  const [businessWallet, setBusinessWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  // Calculate unique locations from mapData
  const uniqueLocations = React.useMemo(() => {
    const siteIds = new Set();
    mapData.forEach(charger => {
      if (charger.site_id) {
        siteIds.add(charger.site_id);
      } else if (charger.latitude && charger.longitude) {
        // Chargers without site but with coordinates count as unique location
        siteIds.add(`${charger.latitude}-${charger.longitude}`);
      }
    });
    return siteIds.size;
  }, [mapData]);

  // Fetch dashboard data
  const fetchData = useCallback(async () => {
    setFetchError(null);
    try {
      const promises = [
        getDashboardSummary(),
        getDashboardKpis(),
        getDashboardCharts(),
        getDashboardMap(),
        getRecentSessions(10)
      ];
      
      // Add business wallet fetching for BUSINESS role
      if (user?.role === 'BUSINESS') {
        promises.push(getBusinessWalletSummary());
      }
      
      const responses = await Promise.all(promises);
      setSummary(responses[0].data);
      setKpis(responses[1].data);
      setCharts(responses[2].data);
      setMapData(responses[3].data || []);
      setRecentSessions(responses[4].data || []);
      
      if (user?.role === 'BUSINESS' && responses[5]) {
        setBusinessWallet(responses[5].data);
      }
      
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setFetchError('Gabim në ngarkimin e të dhënave. Provo sërish.');
      toast.error('Gabim në ngarkimin e të dhënave');
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  // Throttled fetch to prevent excessive API calls
  const throttledFetchData = useCallback(
    (() => {
      let lastCall = 0;
      return () => {
        const now = Date.now();
        if (now - lastCall >= 10000) { // Minimum 10 seconds between calls
          lastCall = now;
          fetchData();
        }
      };
    })(),
    [fetchData]
  );

  // WebSocket connection
  const { connected: wsConnected, connectionStatus, subscribe } = useWebSocket();
  
  // Track if component is mounted to avoid memory leaks
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);
  
  // Polling fallback when WebSocket is not connected - DISABLED to reduce RAM
  // Only manual refresh available when WS is disconnected
  useEffect(() => {
    if (!wsConnected && connectionStatus !== 'connecting') {
      // Reduced polling - only every 120 seconds (was 60)
      const pollInterval = setInterval(() => {
        if (isMounted.current) {
          console.log('[Dashboard] Polling fallback - WS disconnected');
          fetchData();
        }
      }, 120000);
      
      return () => clearInterval(pollInterval);
    }
  }, [wsConnected, connectionStatus, fetchData]);
  
  // Handle WebSocket messages - OPTIMIZED: only listen to critical events
  useEffect(() => {
    let fetchTimeout = null;
    let pendingFetch = false;
    
    const debouncedFetch = () => {
      if (pendingFetch || !isMounted.current) return;
      pendingFetch = true;
      
      if (fetchTimeout) clearTimeout(fetchTimeout);
      fetchTimeout = setTimeout(() => {
        if (isMounted.current) {
          fetchData();
        }
        pendingFetch = false;
      }, 5000); // Wait 5 seconds (was 2) before fetching to batch updates
    };
    
    // Only subscribe to critical events, not '*'
    const criticalEvents = ['charger.created', 'charger.deleted', 'site.created', 'site.deleted', 'session.started', 'session.completed'];
    
    const unsubscribe = subscribe('*', (event) => {
      if (!isMounted.current) return;
      
      const eventType = event.type;
      const data = event.data || {};
      
      // Only update timestamp for critical events
      if (criticalEvents.includes(eventType)) {
        setLastUpdate(new Date());
        debouncedFetch();
        return;
      }
      
      // Handle charger status updates locally without full refresh
      if (eventType === 'charger.updated' || eventType === 'charger.status') {
        if (data.charger_id || data.id) {
          setMapData(prev => prev.map(charger => 
            (charger.charger_id === data.charger_id || charger.id === data.id)
              ? { ...charger, ...data }
              : charger
          ));
        }
        return;
      }
      
      // Ignore frequent session updates to reduce memory pressure
      // session.updated events are very frequent during charging
    });
    
    return () => {
      unsubscribe();
      if (fetchTimeout) clearTimeout(fetchTimeout);
    };
  }, [subscribe, fetchData]);

  // Auto-refresh every 30 seconds for live stats
  useEffect(() => {
    const autoRefresh = setInterval(() => {
      fetchData();
    }, 30000);
    return () => clearInterval(autoRefresh);
  }, [fetchData]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Convert charts data for display
  const sessionsChartData = charts?.sessions_by_day?.map(d => ({
    name: d.day_name,
    seanca: d.sessions,
    teardhura: d.revenue_lek
  })) || [];

  const chargerTypeData = charts?.charge_points_by_type?.map(d => ({
    name: d.type,
    value: d.count
  })) || [];

  const cityData = charts?.locations_by_city?.map(d => ({
    name: d.city,
    value: d.count
  })) || [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="stat-card">
              <div className="skeleton h-4 w-24 mb-4" />
              <div className="skeleton h-8 w-32" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Show error state with retry button
  if (fetchError && !summary) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertTriangle className="w-16 h-16 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Gabim në Lidhje</h2>
        <p className="text-zinc-400 mb-6 text-center max-w-md">{fetchError}</p>
        <Button onClick={fetchData} variant="outline">
          <RefreshCcw className="w-4 h-4 mr-2" />
          Provo Sërish
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground font-outfit">Paneli Kryesor</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Mirë se vini, {user?.full_name}
            {user?.role === 'MODERATOR' && (
              <span className="ml-2 text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                Vetëm karikuesit tuaj
              </span>
            )}
            {user?.role === 'BUSINESS' && (
              <span className="ml-2 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                Llogari Biznesi
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className={`flex items-center gap-2 text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-full transition-all ${
            wsConnected 
              ? 'bg-green-500/20 text-green-400 animate-pulse-glow' 
              : 'bg-red-500/20 text-red-400'
          }`}>
            <span className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-400' : 'bg-red-400'}`} />
            <span className="hidden sm:inline">{wsConnected ? 'Live' : 'Offline'}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchData} className="btn-animated text-xs sm:text-sm" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Rifresko</span>
          </Button>
        </div>
      </div>

      {/* Business Wallet Card - Only for BUSINESS role */}
      {user?.role === 'BUSINESS' && businessWallet && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-500" />
              Portofoli i Biznesit
            </CardTitle>
            <CardDescription>Bilancet dhe alokimet</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Main Wallet Balance */}
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-sm text-muted-foreground mb-1">Bilanci i Biznesit</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {formatCredits(businessWallet?.business_wallet?.balance || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Alokuar në total: {formatCredits(businessWallet?.business_wallet?.allocated_total || 0)}
                </p>
              </div>
              
              {/* Employee Wallets */}
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <p className="text-sm text-muted-foreground mb-1">Bilanci i Punonjësve</p>
                <p className="text-2xl font-bold text-blue-400">
                  {formatCredits(businessWallet?.employee_wallets?.total_balance || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {businessWallet?.employee_wallets?.count || 0} punonjës
                </p>
              </div>
              
              {/* Available Balance */}
              <div className="p-4 rounded-lg bg-violet-500/10 border border-violet-500/20">
                <p className="text-sm text-muted-foreground mb-1">Bilanci i Disponueshëm</p>
                <p className="text-2xl font-bold text-violet-400">
                  {formatCredits(
                    (businessWallet?.business_wallet?.balance || 0) - 
                    (businessWallet?.business_wallet?.allocated_total || 0)
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Gati për alokuar
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards Row 1 - Main Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <div className="stat-card card-hover stagger-item cursor-pointer" onClick={() => navigate('/chargers')} data-testid="card-chargers">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Karikues</p>
              <p className="text-lg sm:text-2xl font-semibold text-foreground font-outfit">{summary?.total_chargers || 0}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground truncate">{summary?.online_chargers || 0} online</p>
        </div>

        <div className="stat-card card-hover stagger-item cursor-pointer" onClick={() => navigate('/sessions')} data-testid="card-active-sessions">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
              <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Seanca Aktive</p>
              <p className="text-lg sm:text-2xl font-semibold text-foreground font-outfit">{kpis?.active_sessions?.count || 0}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground truncate">Duke karikuar</p>
        </div>

        <div className="stat-card card-hover stagger-item cursor-pointer" onClick={() => navigate('/clients')} data-testid="card-unique-users">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-violet-500/10 flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-violet-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Klientë</p>
              <p className="text-lg sm:text-2xl font-semibold text-foreground font-outfit">{summary?.total_clients || 0}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground truncate">Individë & Biznese</p>
        </div>

        <div className="stat-card card-hover stagger-item cursor-pointer" onClick={() => navigate('/sites')} data-testid="card-sites">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-teal-500/10 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-teal-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Stacione</p>
              <p className="text-lg sm:text-2xl font-semibold text-foreground font-outfit">{summary?.total_sites || uniqueLocations || 0}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground truncate">{summary?.total_chargers || 0} karikues</p>
        </div>
      </div>

      {/* Revenue, Credits Used & Energy Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
        <div 
          className="stat-card card-hover stagger-item cursor-pointer"
          onClick={() => navigate('/analytics')}
          data-testid="card-revenue"
        >
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
              <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Të Ardhurat (Topup)</p>
              <p className="text-lg sm:text-2xl font-semibold text-foreground font-outfit">
                {formatLek(kpis?.totals?.revenue_lek || kpis?.revenue?.total_lek || 0)}
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground truncate">
            Sot: {formatLek(kpis?.today_stats?.revenue_lek || kpis?.today?.revenue_lek || 0)}
          </p>
        </div>

        <div 
          className="stat-card card-hover stagger-item cursor-pointer"
          onClick={() => navigate('/analytics')}
          data-testid="card-credits-used"
        >
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
              <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Kredite të Përdorura</p>
              <p className="text-lg sm:text-2xl font-semibold text-foreground font-outfit">
                {formatCredits(kpis?.totals?.credits_used_lek || 0)}
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground truncate">
            Sot: {formatCredits(kpis?.today_stats?.credits_used_lek || 0)}
          </p>
        </div>
        
        <div 
          className="stat-card card-hover stagger-item cursor-pointer"
          onClick={() => navigate('/analytics')}
          data-testid="card-energy"
        >
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
              <Battery className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Energji</p>
              <p className="text-lg sm:text-2xl font-semibold text-foreground font-outfit">
                {formatEnergy(kpis?.totals?.energy_kwh || kpis?.energy?.total_kwh || 0)}
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground truncate">
            Sot: {formatEnergy(kpis?.today_stats?.energy_kwh || kpis?.today?.energy_kwh || 0)}
          </p>
        </div>

        <div 
          className="stat-card card-hover stagger-item cursor-pointer"
          onClick={() => navigate('/sessions')}
          data-testid="card-total-sessions"
        >
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Seanca</p>
              <p className="text-lg sm:text-2xl font-semibold text-foreground font-outfit">
                {kpis?.totals?.sessions || kpis?.total_sessions?.count || 0}
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground truncate">
            Sot: {kpis?.today_stats?.sessions || kpis?.today?.sessions || 0}
          </p>
        </div>

        <div 
          className="stat-card card-hover stagger-item cursor-pointer"
          onClick={() => navigate('/chargers')}
          data-testid="card-problems"
        >
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              (kpis?.faults_connectivity?.total_issues || 0) > 0 
                ? 'bg-red-500/20' 
                : 'bg-green-500/20'
            }`}>
              <AlertTriangle className={`w-4 h-4 sm:w-5 sm:h-5 ${
                (kpis?.faults_connectivity?.total_issues || 0) > 0 
                  ? 'text-red-500' 
                  : 'text-green-500'
              }`} />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Probleme</p>
              <p className="text-lg sm:text-2xl font-semibold text-foreground font-outfit">
                {kpis?.faults_connectivity?.total_issues || 0}
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {kpis?.faults_connectivity?.faulted || 0} faulted, {kpis?.faults_connectivity?.offline || 0} offline
          </p>
        </div>
      </div>

      {/* Map - Full Width */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2 px-3 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <CardTitle className="text-sm sm:text-base text-foreground font-outfit flex items-center gap-2">
                <Map className="w-4 h-4 sm:w-5 sm:h-5 text-[#afd039]" />
                Harta e Stacioneve
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                {uniqueLocations} vendndodhje, {mapData.length} karikues
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 text-xs flex-wrap">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-[#afd039]"></span>
                Online
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                Pjesërisht
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                Offline
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          <div className="h-[300px] sm:h-[350px] md:h-[400px] rounded-xl overflow-hidden">
            <Suspense fallback={<div className="h-full flex items-center justify-center bg-zinc-800/50"><div className="animate-spin w-8 h-8 border-2 border-[#1d62a7] border-t-transparent rounded-full" /></div>}>
              <ChargerMap 
                sites={mapData.reduce((acc, charger) => {
                  if (!acc.find(s => s.id === charger.site_id)) {
                    acc.push({
                      id: charger.site_id,
                      name: charger.site_name,
                      address: charger.address,
                      latitude: charger.latitude,
                      longitude: charger.longitude
                    });
                  }
                  return acc;
                }, [])} 
                chargers={mapData.map(c => ({
                  id: c.charger_id,
                  site_id: c.site_id,
                  charger_id: c.charger_name,
                  status: c.status || (isChargerOnline(c) ? 'Online' : 'Offline'),
                  is_online: isChargerOnline(c)
                }))} 
              />
            </Suspense>
          </div>
        </CardContent>
      </Card>

      {/* 3 Columns Row: Live Sessions + Revenue Chart + Connector Types */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Live Sessions Monitor */}
        <div data-testid="live-sessions-section">
          <Suspense fallback={<Card className="bg-card border-border h-[350px] flex items-center justify-center"><div className="animate-spin w-6 h-6 border-2 border-[#1d62a7] border-t-transparent rounded-full" /></Card>}>
            <LiveSessionsMonitor 
              onOpenAll={() => navigate('/sessions')}
              onSessionClick={(session) => navigate(`/sessions?search=${encodeURIComponent(session.id || '')}`)}
            />
          </Suspense>
        </div>

        {/* Revenue Chart - Simplified Bar Display */}
        <Card className="bg-card border-border cursor-pointer hover:bg-accent/5 transition-colors" onClick={() => navigate('/analytics')}>
          <CardHeader className="px-3 sm:px-6">
            <CardTitle className="text-sm sm:text-base text-foreground font-outfit">Të ardhurat javore</CardTitle>
            <CardDescription className="text-xs sm:text-sm">Performanca e 7 ditëve të fundit</CardDescription>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            <div className="h-[200px] sm:h-[250px] md:h-[280px] flex items-end gap-2 justify-between px-2">
              {sessionsChartData.map((day, idx) => {
                const maxVal = Math.max(...sessionsChartData.map(d => d.teardhura || 0), 1);
                const height = ((day.teardhura || 0) / maxVal) * 100;
                return (
                  <div key={idx} className="flex flex-col items-center flex-1 max-w-[60px]">
                    <div 
                      className="w-full bg-[#1d62a7]/80 rounded-t-md transition-all hover:bg-[#1d62a7] cursor-pointer"
                      style={{ height: `${Math.max(height, 5)}%`, minHeight: '8px' }}
                      title={`${day.name}: ${Number(day.teardhura || 0).toFixed(0)} Lek`}
                    />
                    <span className="text-[10px] text-muted-foreground mt-2 truncate w-full text-center">{day.name}</span>
                    <span className="text-[9px] text-muted-foreground/70">{Number(day.teardhura || 0).toFixed(0)} Lek</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Connector Types - Simple List instead of PieChart */}
        <Card className="bg-card border-border">
          <CardHeader className="px-3 sm:px-6">
            <CardTitle className="text-sm sm:text-base text-foreground font-outfit">Llojet e Konektorëve</CardTitle>
            <CardDescription className="text-xs sm:text-sm">Shpërndarja sipas tipit</CardDescription>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="space-y-3">
              {chargerTypeData.length > 0 ? (
                chargerTypeData.map((entry, index) => {
                  const total = chargerTypeData.reduce((sum, e) => sum + e.value, 0);
                  const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : 0;
                  return (
                    <div key={entry.name} className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-foreground truncate">{entry.name}</span>
                          <span className="text-xs text-muted-foreground ml-2">{entry.value} ({percentage}%)</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all"
                            style={{ 
                              width: `${percentage}%`, 
                              backgroundColor: COLORS[index % COLORS.length] 
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
                  Nuk ka të dhëna
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Sessions */}
      <Card className="bg-card border-border">
        <CardHeader className="px-3 sm:px-6">
          <CardTitle className="text-sm sm:text-base text-foreground font-outfit">Seancat e fundit</CardTitle>
          <CardDescription className="text-xs sm:text-sm">10 seancat më të fundit të karikimit</CardDescription>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground text-xs whitespace-nowrap">Përdoruesi</TableHead>
                  <TableHead className="text-muted-foreground text-xs whitespace-nowrap hidden sm:table-cell">Vendndodhja</TableHead>
                  <TableHead className="text-muted-foreground text-xs whitespace-nowrap">Karikuesi</TableHead>
                  <TableHead className="text-muted-foreground text-xs whitespace-nowrap">Energjia</TableHead>
                  <TableHead className="text-muted-foreground text-xs whitespace-nowrap hidden md:table-cell">Kostoja</TableHead>
                  <TableHead className="text-muted-foreground text-xs whitespace-nowrap">Statusi</TableHead>
                  <TableHead className="text-muted-foreground text-xs whitespace-nowrap hidden lg:table-cell">Koha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentSessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8 text-sm">
                      Nuk ka seanca të regjistruara
                    </TableCell>
                  </TableRow>
                ) : (
                  recentSessions.map((session) => (
                    <TableRow key={session.id} className="border-border/50 hover:bg-accent/5">
                      <TableCell className="py-2 sm:py-3">
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm text-foreground truncate max-w-[100px] sm:max-w-[150px]">{session.user_name || '—'}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[100px] sm:max-w-[150px] hidden sm:block">{session.user_email || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-foreground/80 text-xs sm:text-sm hidden sm:table-cell">
                        <span className="truncate block max-w-[100px]">{session.site_name || '—'}</span>
                      </TableCell>
                      <TableCell>
                        <code className="id-field text-xs">{session.charger_name || '—'}</code>
                      </TableCell>
                      <TableCell className="text-foreground/80 text-xs sm:text-sm whitespace-nowrap">
                        {formatEnergy(session.energy_kwh)}
                      </TableCell>
                      <TableCell className="text-foreground/80 text-xs sm:text-sm whitespace-nowrap hidden md:table-cell">
                        {formatCredits(session.cost_lek)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={session.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs hidden lg:table-cell whitespace-nowrap">
                        {formatDate(session.start_time)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Last Update */}
      {lastUpdate && (
        <p className="text-xs text-muted-foreground text-center">
          Përditësimi i fundit: {lastUpdate.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
        </p>
      )}
    </div>
  );
};

export default Dashboard;
